import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Card, CardProps, Link } from '@common/material'
import {
  RegisterItem,
  RegisterItemFlatPropertyData,
} from '@core/entities/register/RegisterItem'
import useConstants from '@core/providers/useConstants'
import {
  Cancel,
  CheckCircle,
  DoNotDisturbOn,
  Launch,
} from '@mui/icons-material'
import moment from 'moment/moment'
import { RegisterInspection } from '@core/entities/register/RegisterInspection'
import { localizedStrings } from '@core/strings'
import { RegisterItemStatusSummary } from '@modules/registers/items/RegisterItemStatusSummary'
import { RegisterItemPropertyAttachmentValue } from '@modules/registers/propertyValue/RegisterItemPropertyAttachmentValue'

type ComponentProps = Omit<CardProps, 'id'> & {
  registerItem: RegisterItem
  previewPropertyIds?: Array<string>
  icon?: React.ReactNode
  active?: boolean
  highlight?: string
}

export const RegisterItemCard: React.FC<ComponentProps> = ({
  registerItem,
  previewPropertyIds,
  icon,
  active,
  sx = {},
  highlight,
  ...props
}) => {
  const { constants } = useConstants()
  const descriptionTitle = registerItem.getDescriptionPropertyText()
  const properties = registerItem.getPreviewPropertiesData(
    previewPropertyIds,
    constants.max_register_card_properties,
    highlight,
  )
  const getIcon = (property: RegisterItemFlatPropertyData) => {
    if (property.type === 'SELECT' && property.selectedOption?.colour) {
      return (
        <Box
          bgcolor={property.selectedOption?.colour}
          borderRadius={100}
          width={14}
          height={14}
          mr={1}
        />
      )
    }

    if (property.type === 'INSPECTION') {
      if (!property.inspection?.lastInspectedBy) {
        return (
          <DoNotDisturbOn
            sx={{ fontSize: '16px', color: 'warning.main', mr: 1 }}
          />
        )
      } else if (moment().isAfter(property.inspection?.nextInspectionDue)) {
        return <Cancel sx={{ fontSize: '16px', mr: 1 }} color='error' />
      } else {
        return <CheckCircle sx={{ fontSize: '16px', mr: 1 }} color='success' />
      }
    }
    return <></>
  }

  const getColor = (property: RegisterItemFlatPropertyData) => {
    if (property.type === 'INSPECTION') {
      return RegisterInspection.getDueDateColor(
        property.inspection?.nextInspectionDue,
      )
    }
    return 'auto'
  }

  return (
    <Card
      sx={{
        p: 2,
        my: 1,
        ...sx,
      }}
      {...props}
    >
      <Flex>
        <Box flex={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Text fontSize={13} color='text.secondary'>
              {registerItem.uniqueId}
            </Text>
            {!!descriptionTitle && (
              <Text fontWeight={500} fontSize='1rem' highlight={highlight}>
                {descriptionTitle}
              </Text>
            )}
            <Box mt={1}>
              {properties?.map((property) => (
                <Flex
                  key={property.registerPropertyId}
                  alignItems='center'
                  sx={{ gap: 3 }}
                >
                  <Flex alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
                    <Text
                      color='text.secondary'
                      sx={{
                        fontSize: 14,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={property.name}
                    >
                      {property.name}
                    </Text>
                  </Flex>
                  <Flex alignItems='center' sx={{ flex: 3, minWidth: 0 }}>
                    {property.type === 'ATTACHMENT' ? (
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 0.5,
                        }}
                      >
                        <RegisterItemPropertyAttachmentValue
                          registerItemPropertyData={property}
                        />
                      </Box>
                    ) : (
                      <>
                        {getIcon(property)}
                        <Text
                          sx={{ fontSize: 14, flex: 1, minWidth: 0 }}
                          highlight={highlight}
                          color={getColor(property)}
                        >
                          {registerItem.isUrl(property) ? (
                            <Link
                              href={registerItem.getRegisterItemPropertyDisplayValues(
                                property,
                              )}
                              target='_blank'
                            >
                              {`${localizedStrings.open} `}
                              <Launch sx={{ fontSize: '16px' }} />
                            </Link>
                          ) : (
                            registerItem.getRegisterItemPropertyDisplayValues(
                              property,
                            )
                          )}
                        </Text>
                      </>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Box>
            <RegisterItemStatusSummary registerItem={registerItem} />
          </Box>
        </Box>
        {icon && <Box>{icon}</Box>}
      </Flex>
    </Card>
  )
}
